import { ReactNode, useEffect, useState } from 'react';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { usePolicyAcknowledgment } from '@/hooks/usePolicyAcknowledgment';
import { PolicyAcknowledgmentModal } from './PolicyAcknowledgmentModal';
import { PendingParentalApproval } from './PendingParentalApproval';
import { useToast } from '@/hooks/use-toast';
import { policyAcknowledgmentService } from '@/services/policyAcknowledgmentService';

interface PolicyAcknowledgmentGuardProps {
  children: ReactNode;
}

export const PolicyAcknowledgmentGuard = ({ children }: PolicyAcknowledgmentGuardProps) => {
  const { user, isLoading: userLoading } = useUnifiedUser();
  const { toast } = useToast();
  
  const {
    needsAcknowledgment,
    requiresParentalConsent,
    isLoading: policyLoading,
    refreshStatus
  } = usePolicyAcknowledgment(
    user?.id || null,
    user?.userType || null
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pendingParentalVerification, setPendingParentalVerification] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Track when initial load completes to prevent loading flicker on subsequent updates
  useEffect(() => {
    if (!userLoading && !policyLoading && !hasInitialized) {
      setHasInitialized(true);
    }
  }, [userLoading, policyLoading, hasInitialized]);

  // Check if child has acknowledged but parent hasn't
  useEffect(() => {
    const checkConsentStatus = async () => {
      if (!user?.id || !user?.userType) return;
      
      const { data } = await policyAcknowledgmentService.checkChildConsentStatus(
        user.id, 
        user.userType
      );
      
      // If child acknowledged but parent hasn't, show pending screen
      if (data?.childAcknowledged && !data?.parentAcknowledged) {
        setPendingParentalVerification(true);
      } else {
        setPendingParentalVerification(false);
      }
    };

    if (!userLoading && !policyLoading && user) {
      checkConsentStatus();
    }
  }, [userLoading, policyLoading, user, needsAcknowledgment]);

  // Show modal when acknowledgment is needed and loading is complete
  useEffect(() => {
    if (!userLoading && !policyLoading && user && needsAcknowledgment && !pendingParentalVerification) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [userLoading, policyLoading, user, needsAcknowledgment, pendingParentalVerification]);

  const handleSubmit = async (data: {
    privacyPolicyAccepted: boolean;
    tosAccepted: boolean;
    cookiesAccepted: boolean;
    dataRightsAcknowledged: boolean;
    isMinorAcknowledgmentOnly?: boolean;
  }): Promise<{ success: boolean; error?: any }> => {
    if (!user) return { success: false, error: 'No user' };
    
    setIsSubmitting(true);
    
    try {
      // For minors, use the child acknowledgment flow (internal, no email)
      if (data.isMinorAcknowledgmentOnly) {
        const result = await policyAcknowledgmentService.submitChildAcknowledgment({
          userId: user.id,
          userType: user.userType,
          privacyPolicyAccepted: data.privacyPolicyAccepted,
          tosAccepted: data.tosAccepted,
          cookiesAccepted: data.cookiesAccepted,
          dataRightsAcknowledged: data.dataRightsAcknowledged,
          userAgent: navigator.userAgent
        });

        if (result.success) {
          setPendingParentalVerification(true);
          toast({
            title: "Acknowledgment Saved!",
            description: "A notification has been sent to your parent. Share your Connection Code with them.",
          });
          setShowModal(false);
          return { success: true };
        } else {
          toast({
            title: "Error",
            description: "Failed to save acknowledgment. Please try again.",
            variant: "destructive"
          });
          return { success: false, error: result.error };
        }
      }

      // For adults, use standard acknowledgment flow
      const { data: versions } = await policyAcknowledgmentService.getLatestPolicyVersions();
      const privacyVersion = versions?.find((v: any) => v.policy_type === 'privacy_policy')?.version || '1.0';
      const tosVersion = versions?.find((v: any) => v.policy_type === 'terms_of_service')?.version || '1.0';

      const result = await policyAcknowledgmentService.submitAcknowledgment({
        userId: user.id,
        userType: user.userType,
        privacyPolicyAccepted: data.privacyPolicyAccepted,
        tosAccepted: data.tosAccepted,
        cookiesAccepted: data.cookiesAccepted,
        dataRightsAcknowledged: data.dataRightsAcknowledged,
        userAgent: navigator.userAgent
      });

      if (!result.error) {
        toast({
          title: "Legal Agreements Accepted",
          description: "You can now access Players Lockeroom. Welcome!",
        });
        await refreshStatus();
        setShowModal(false);
        return { success: true };
      } else {
        toast({
          title: "Error",
          description: "Failed to save your acknowledgment. Please try again.",
          variant: "destructive"
        });
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error submitting acknowledgment:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
      return { success: false, error };
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefreshStatus = async () => {
    await refreshStatus();
    if (user?.id && user?.userType) {
      const { data } = await policyAcknowledgmentService.checkChildConsentStatus(
        user.id,
        user.userType
      );
      if (data?.parentAcknowledged) {
        setPendingParentalVerification(false);
        toast({
          title: "Consent Verified!",
          description: "Your parent has provided their consent. Welcome to Players Lockeroom!",
        });
      } else {
        toast({
          title: "Still Waiting",
          description: "Your parent has not completed their consent yet. Please ask them to check their notifications.",
        });
      }
    }
  };

  // If no user, just render children immediately (landing page handles its own loading)
  if (!user) {
    return <>{children}</>;
  }

  // Show loading state only on initial load, not on subsequent updates
  if ((userLoading || policyLoading) && !hasInitialized) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If pending parental verification, show waiting screen with connection code
  if (pendingParentalVerification) {
    return (
      <PendingParentalApproval
        childUserId={user.id}
        childUsername={user.username || user.email || 'Unknown'}
        onRefreshStatus={handleRefreshStatus}
        isSubmitting={isSubmitting}
      />
    );
  }

  // If acknowledgment is needed, show modal and block access
  if (showModal) {
    return (
      <>
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              {requiresParentalConsent ? 'Parent Approval Required' : 'Legal Agreements Required'}
            </h2>
            <p className="text-muted-foreground">
              {requiresParentalConsent 
                ? 'Since you are under 13, your parent or guardian must provide consent before you can access Players Lockeroom.'
                : 'Before accessing Players Lockeroom, you must review and accept our legal agreements.'}
            </p>
          </div>
        </div>
        <PolicyAcknowledgmentModal
          open={showModal}
          requiresParentalConsent={requiresParentalConsent}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      </>
    );
  }

  // User has acknowledged, render children
  return <>{children}</>;
};
